/**
 * Centralized input sanitization & validation utilities.
 * Used across all user-facing forms to prevent XSS, injection, and spam.
 */

// Strip HTML tags and dangerous characters
export function sanitizeText(value: string, maxLength = 200): string {
  return value
    .replace(/<[^>]*>/g, '')           // strip HTML tags
    .replace(/[<>"'`]/g, '')           // remove dangerous chars
    .replace(/javascript:/gi, '')      // block JS protocol
    .replace(/on\w+=/gi, '')           // block inline event handlers
    .replace(/\s+/g, ' ')             // collapse whitespace
    .trim()
    .slice(0, maxLength);
}

// Validate city name: only letters, spaces, hyphens, apostrophes, periods
const CITY_NAME_REGEX = /^[\p{L}\p{M}\s\-'.()]+$/u;
const MIN_CITY_LENGTH = 2;
const MAX_CITY_LENGTH = 80;

export function sanitizeCityName(value: string): string {
  return sanitizeText(value, MAX_CITY_LENGTH);
}

export function isValidCityName(value: string): { valid: boolean; reason?: string } {
  const cleaned = value.trim();
  if (cleaned.length < MIN_CITY_LENGTH) {
    return { valid: false, reason: 'Stadsnaam is te kort.' };
  }
  if (cleaned.length > MAX_CITY_LENGTH) {
    return { valid: false, reason: 'Stadsnaam is te lang.' };
  }
  if (!CITY_NAME_REGEX.test(cleaned)) {
    return { valid: false, reason: 'Stadsnaam bevat ongeldige tekens.' };
  }
  // Block obvious spam patterns
  if (/(.)\1{4,}/.test(cleaned)) {
    return { valid: false, reason: 'Ongeldige stadsnaam.' };
  }
  // Block URLs
  if (/https?:\/\//i.test(cleaned) || /www\./i.test(cleaned)) {
    return { valid: false, reason: 'URLs zijn niet toegestaan.' };
  }
  return { valid: true };
}

// Email validation
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAIL_LENGTH = 255;

export function sanitizeEmail(value: string): string {
  return value.trim().toLowerCase().slice(0, MAX_EMAIL_LENGTH);
}

export function isValidEmail(value: string): boolean {
  if (!value) return true; // optional field
  return EMAIL_REGEX.test(value.trim()) && value.trim().length <= MAX_EMAIL_LENGTH;
}

// Person name validation
const MAX_NAME_LENGTH = 100;

export function sanitizeName(value: string): string {
  return sanitizeText(value, MAX_NAME_LENGTH);
}

export function isValidName(value: string): { valid: boolean; reason?: string } {
  const cleaned = value.trim();
  if (!cleaned) return { valid: true }; // often optional
  if (cleaned.length > MAX_NAME_LENGTH) {
    return { valid: false, reason: 'Naam is te lang.' };
  }
  if (/https?:\/\//i.test(cleaned) || /www\./i.test(cleaned)) {
    return { valid: false, reason: 'URLs zijn niet toegestaan.' };
  }
  if (/(.)\1{4,}/.test(cleaned)) {
    return { valid: false, reason: 'Ongeldige naam.' };
  }
  return { valid: true };
}

// Message / free text validation
const MAX_MESSAGE_LENGTH = 1000;

export function sanitizeMessage(value: string): string {
  return sanitizeText(value, MAX_MESSAGE_LENGTH);
}

// Phone validation (basic)
const PHONE_REGEX = /^[+\d\s\-()]{6,20}$/;

export function isValidPhone(value: string): boolean {
  if (!value) return true;
  return PHONE_REGEX.test(value.trim());
}

// Instagram handle validation
export function sanitizeInstagramHandle(value: string): string {
  return value.trim().replace(/[^a-zA-Z0-9._@]/g, '').slice(0, 30);
}

// Simple client-side rate limiting
const rateLimitMap = new Map<string, number[]>();

export function checkRateLimit(key: string, maxPerMinute = 3): boolean {
  const now = Date.now();
  const windowMs = 60_000;
  const timestamps = rateLimitMap.get(key) || [];
  const recent = timestamps.filter(t => now - t < windowMs);
  if (recent.length >= maxPerMinute) return false;
  recent.push(now);
  rateLimitMap.set(key, recent);
  return true;
}
